import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, ShieldCheck, Sliders, Send, MessageCircle,
  Compass, FileBadge2, Brain, Calculator, Bell, ImageIcon, Globe2,
  Inbox, BadgeCheck, Gavel, Share2, FileCheck2, Receipt, BarChart3,
  Megaphone, Sparkle, Mailbox, Users2, ClipboardList,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import PressStrip from './PressStrip';
import { useABVariant } from '../hooks/useABVariant';
import { useGeoBucket } from '../hooks/useGeoBucket';
import { useMarket } from '../hooks/useMarket';

/**
 * Features Hero Card — the revenue-conversion anchor.
 *
 * Market-aware: uses `useMarket()` to build every feature copy string so
 * "Luas", "BER", "€" and "Eircode" auto-swap to "Tube", "EPC", "£" and
 * "postcode" on the UK bucket (and "subway", "Home Energy Score", "$",
 * "ZIP" on US).
 */

const buildBuyerFeatures = (market) => {
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const energyLabel = isUK ? 'EPC' : isUS ? 'Home Energy Score' : 'BER';
  const registerReg = isUK ? 'the government EPC register' : isUS ? 'the DOE Home Energy Score system' : 'SEAI';
  const licensingBody = isUK ? 'Propertymark' : isUS ? 'State board' : 'PSRA';
  const transit = isUK ? 'Tube' : isUS ? 'subway' : 'Luas';
  const postal = isUK ? 'postcode' : isUS ? 'ZIP code' : 'Eircode';
  const stylesLine = isUK ? 'Six UK styles' : isUS ? 'Six US styles' : 'Six Irish-relevant looks';
  return [
    {
      icon: Brain,
      title: 'AI Property Assistant',
      body: 'Ask anything about any listing. Grounded on real Passport data — no hallucinations.',
      accent: 'purple',
      isNew: false,
    },
    {
      icon: FileBadge2,
      title: 'Property Passport',
      body: `Six-section dossier per home — ${energyLabel}, planning, neighbours, real costs — ${postal}-driven.`,
      accent: 'indigo',
      isNew: true,
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp price-drop alerts',
      body: '98% open rate. Hit the second your shortlisted home drops in price.',
      accent: 'emerald',
      isNew: true,
    },
    {
      icon: Send,
      title: 'Send My Shortlist',
      body: 'One link to share your 3 favourite homes with partner, parents, builder — no login for them.',
      accent: 'fuchsia',
      isNew: true,
    },
    {
      icon: Sliders,
      title: 'Honest Read score',
      body: `AI decodes the agent copy. "Walk to ${transit}" really means 22 min. One score, zero spin.`,
      accent: 'rose',
    },
    {
      icon: Calculator,
      title: 'Negotiation IQ',
      body: 'Opening bid + ceiling grounded in the last 24 months of actual sold prices on the road. Bring evidence, not a hunch.',
      accent: 'amber',
    },
    {
      icon: Compass,
      title: 'Real Price Index',
      body: `Actual sold prices by ${postal}, refreshed monthly. Spot a soft market before the agent does.`,
      accent: 'sky',
    },
    {
      icon: ImageIcon,
      title: 'Design My Room (AI)',
      body: `Snap a photo, pick a style, see the room transform. ${stylesLine}. 3/day free.`,
      accent: 'fuchsia',
    },
    {
      icon: Bell,
      title: 'Lock-screen push alerts',
      body: 'Install lovinahome as an app. Saved-search matches land on your home screen.',
      accent: 'purple',
    },
    {
      icon: ShieldCheck,
      title: 'Verified listings',
      body: `${energyLabel} cross-checked against ${registerReg}. ${licensingBody} agent badge pulled live. No marketing claims.`,
      accent: 'emerald',
    },
    {
      icon: Sparkle,
      title: `${isUK || isUS ? 'Rightmove' : 'Daft.ie'} one-click import`,
      body: `Paste your ${isUK || isUS ? 'Rightmove' : 'Daft'} saved-searches. We recreate them on lovinahome in seconds.`,
      accent: 'cyan',
      isNew: true,
    },
    {
      icon: Globe2,
      title: 'Encrypted in-thread offers',
      body: 'Make a real offer in chat. AES-256 at rest. Calendar invites for viewings built in.',
      accent: 'indigo',
    },
  ];
};

const buildAgentFeatures = (market) => {
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const licensingBody = isUK ? 'Propertymark' : isUS ? 'State' : 'PSRA';
  const licenceExample = isUK ? 'Propertymark #XXXX ✓' : isUS ? 'Lic. #XXXXX ✓' : 'PSRA #XXXX ✓';
  const energyLabel = isUK ? 'EPC' : isUS ? 'Home Energy Score' : 'BER';
  const postal = isUK ? 'postcode' : isUS ? 'ZIP' : 'Eircode';
  const currencySymbol = market.currency_symbol || '€';
  return [
    {
      icon: Inbox,
      title: 'AI Suggest Replies',
      body: 'GPT writes three on-brand first replies in one tap. Buyers never know. Reply time → seconds.',
      accent: 'purple',
    },
    {
      icon: Gavel,
      title: 'Open Bidding (vendor-controlled)',
      body: `Anonymous "highest ${currencySymbol}X · N active bidders" in chat. Off by default — flip for the right vendor.`,
      accent: 'amber',
    },
    {
      icon: FileCheck2,
      title: 'Offers + viewings in-thread',
      body: 'Accept · Decline · Counter. Propose five viewing slots. .ics invites auto-fire to both calendars.',
      accent: 'emerald',
    },
    {
      icon: Share2,
      title: 'Dynamic share cards',
      body: 'Every listing becomes a viral asset. 1080×1920 IG Story export in one tap.',
      accent: 'fuchsia',
    },
    {
      icon: BadgeCheck,
      title: `${licensingBody} badge auto-pulled`,
      body: `Your licence number rendered on every listing. Buyers see "${licenceExample}" — trust signal in 0.2s.`,
      accent: 'emerald',
      isNew: true,
    },
    {
      icon: Megaphone,
      title: 'Viral Content Engine',
      body: 'Auto-generated branded posts about your new listings — to your social, on a schedule.',
      accent: 'rose',
    },
    {
      icon: BarChart3,
      title: 'Listing analytics',
      body: 'Daily views, save-rate, ask-rate, offer funnel. Pin the laggards before vendor calls.',
      accent: 'sky',
    },
    {
      icon: Mailbox,
      title: 'Direct enquiries — zero lead-resale',
      body: 'Buyer messages land in YOUR inbox. No funnel, no transfer, no "we have a buyer for you" cold calls.',
      accent: 'emerald',
    },
    {
      icon: Receipt,
      title: 'Stripe billing built in',
      body: 'Free listing tier · one-off boost · monthly subscription. Live tax-compliant invoicing in 6 countries.',
      accent: 'amber',
    },
    {
      icon: Users2,
      title: 'Agency hierarchy + permissions',
      body: 'Principal Agent → Branch → Negotiator. Permissioned roles, audit log on every status change.',
      accent: 'indigo',
    },
    {
      icon: ClipboardList,
      title: 'Bulk listing import',
      body: `CSV or feed (XML/RSS). 200 listings live in 5 minutes. ${postal} + ${energyLabel} auto-resolved.`,
      accent: 'cyan',
    },
    {
      icon: FileBadge2,
      title: 'eIDAS Property Passport',
      body: 'Tamper-evident sale bundle. Next sale on the same property is 60% faster — your reputation compounds.',
      accent: 'indigo',
    },
  ];
};

const ACCENT_CLASSES = {
  purple: 'bg-purple-50 text-[#7042cc] ring-purple-100',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-100',
  rose: 'bg-rose-50 text-rose-700 ring-rose-100',
  amber: 'bg-amber-50 text-amber-700 ring-amber-100',
  sky: 'bg-sky-50 text-sky-700 ring-sky-100',
  cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
};

// Country-bucketed copy. The backend's /api/geo/country decides which
// bucket the visitor falls into (IE / UK / US). The same variant letter
// (A/B/C) is preserved across buckets so cross-country experiments can
// be compared apples-to-apples on the metric, but the actual wording
// changes so the joke lands appropriately.
const HEADLINES_BY_BUCKET = {
  IE: {
    buyer: {
      A: { titleA: 'Buy your home with', titleB: 'eyes wide open.' },
      // User-requested IE-flavour challenger
      B: { titleA: "Don't be Daft.", titleB: 'Lovinahome — the smarter way to buy property.' },
      C: { titleA: 'Find your next home,', titleB: 'no surprises included.' },
      eyebrow: 'For buyers · everything in one app',
      sub:
        'Every Irish listing — decoded, verified and calibrated against real sold prices. Powered by your Property Passport. Honest copy, no spin.',
      primary: { label: 'Browse Irish homes', to: '/listings' },
      secondary: { label: 'How Honest Read works', to: '/how-we-vet-listings' },
      stats: [
        { value: '50,000+', label: 'live Irish listings' },
        { value: '24 mo', label: 'sold price history per road' },
        { value: '6-section', label: 'Property Passport' },
      ],
    },
    agent: {
      A: { titleA: 'Close faster. Keep more.', titleB: 'Look great doing it.' },
      B: { titleA: 'The portal', titleB: 'that pays you back.' },
      C: { titleA: 'Win listings — without', titleB: 'the 12-month contract.' },
      eyebrow: 'For agents · the platform that pays for itself',
      sub:
        'Encrypted chat, in-thread offers, AI reply suggestions, viral share cards, PSRA-verified badges and live analytics — every feature included in your subscription. No lead-resale, ever.',
      primary: { label: 'List free for 30 days', to: '/for-agents' },
      secondary: { label: 'See pricing', to: '/pricing' },
      stats: [
        { value: '< 8 min', label: 'avg reply time' },
        { value: '0%', label: 'leads resold' },
        { value: '60%', label: 'faster re-sales (Passport)' },
      ],
    },
  },
  UK: {
    buyer: {
      A: { titleA: 'Buy your next home with', titleB: 'every fact upfront.' },
      B: { titleA: 'The honest property portal —', titleB: 'no estate-agent spin.' },
      C: { titleA: 'Find your home without', titleB: 'the agent waffle.' },
      eyebrow: 'For UK buyers · everything in one app',
      sub:
        'Every UK listing — decoded, verified and calibrated against real HM Land Registry sold prices. Powered by your Property Passport. No fluff. No spin.',
      primary: { label: 'Browse UK homes', to: '/uk/buy' },
      secondary: { label: 'How Honest Read works', to: '/how-we-vet-listings' },
      stats: [
        { value: 'Live', label: 'UK listings' },
        { value: '24 mo', label: 'HM Land Registry history' },
        { value: '6-section', label: 'Property Passport' },
      ],
    },
    agent: {
      A: { titleA: 'Close faster. Keep more.', titleB: 'Look great doing it.' },
      B: { titleA: 'The portal', titleB: 'that pays you back.' },
      C: { titleA: 'Win instructions — without', titleB: 'the 12-month tie-in.' },
      eyebrow: 'For UK agents · the platform that pays for itself',
      sub:
        'Encrypted chat, in-thread offers, AI reply suggestions, viral share cards, verified badges and live analytics — every feature included in your subscription. No lead-resale, ever.',
      primary: { label: 'List free for 30 days', to: '/for-agents' },
      secondary: { label: 'See pricing', to: '/pricing' },
      stats: [
        { value: '< 8 min', label: 'avg reply time' },
        { value: '0%', label: 'leads resold' },
        { value: '60%', label: 'faster re-sales (Passport)' },
      ],
    },
  },
  US: {
    buyer: {
      A: { titleA: 'Find your next home with', titleB: 'every fact verified.' },
      B: { titleA: 'Real estate,', titleB: 'finally transparent.' },
      C: { titleA: 'Buy smart. Skip', titleB: 'the showroom spin.' },
      eyebrow: 'For buyers · everything in one app',
      sub:
        'Every listing — decoded, verified and calibrated against real sold prices. Powered by your Property Passport. Honest copy, no spin.',
      primary: { label: 'Browse listings', to: '/listings' },
      secondary: { label: 'How Honest Read works', to: '/how-we-vet-listings' },
      stats: [
        { value: '50,000+', label: 'live listings' },
        { value: '24 mo', label: 'MLS + public records history' },
        { value: '6-section', label: 'Property Passport' },
      ],
    },
    agent: {
      A: { titleA: 'Close faster. Keep more.', titleB: 'Look great doing it.' },
      B: { titleA: 'The portal', titleB: 'that pays you back.' },
      C: { titleA: 'Win listings — without', titleB: 'the 12-month contract.' },
      eyebrow: 'For agents · the platform that pays for itself',
      sub:
        'Encrypted chat, in-thread offers, AI reply suggestions, viral share cards, verified badges and live analytics — every feature included in your subscription. No lead-resale, ever.',
      primary: { label: 'List free for 30 days', to: '/for-agents' },
      secondary: { label: 'See pricing', to: '/pricing' },
      stats: [
        { value: '< 8 min', label: 'avg reply time' },
        { value: '0%', label: 'leads resold' },
        { value: '60%', label: 'faster re-sales (Passport)' },
      ],
    },
  },
};

const FeatureTile = ({ feature, idx, audience }) => {
  const Icon = feature.icon;
  const accentCls = ACCENT_CLASSES[feature.accent] || ACCENT_CLASSES.purple;
  return (
    <div
      className="group relative rounded-2xl bg-white p-4 ring-1 ring-gray-200 hover:ring-[#7042cc]/40 hover:shadow-lg transition-all duration-300"
      style={{ animationDelay: `${idx * 35}ms` }}
      data-testid={`hero-feature-${audience}-${idx}`}
    >
      {feature.isNew && (
        <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#7042cc] to-[#a76bf5] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 shadow-sm">
          <Sparkles className="h-2.5 w-2.5" />
          New
        </span>
      )}
      <div
        className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ring-1 ${accentCls} mb-3 group-hover:scale-110 transition-transform`}
      >
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1">
        {feature.title}
      </h3>
      <p className="text-xs text-gray-600 leading-relaxed">{feature.body}</p>
    </div>
  );
};

/**
 * @param {{ defaultAudience?: 'buyer'|'agent', allowToggle?: boolean }} props
 */
export const FeaturesHeroCard = ({ defaultAudience = 'buyer', allowToggle = true }) => {
  const [audience, setAudience] = useState(defaultAudience);
  const market = useMarket();
  const features = audience === 'agent' ? buildAgentFeatures(market) : buildBuyerFeatures(market);
  const { bucket } = useGeoBucket();
  // Prefer the URL-derived market (via useMarket) so /uk visitors always get
  // the UK bucket regardless of geo-detection fallback. Fall back to
  // useGeoBucket for visitors on unprefixed pages (i.e. legacy IE default).
  const marketToBucket = { GB: 'UK', US: 'US', IE: 'IE' };
  const bucketKey = marketToBucket[market.code]
    || (bucket && HEADLINES_BY_BUCKET[bucket] ? bucket : 'IE');
  const baseCopy = HEADLINES_BY_BUCKET[bucketKey][audience];
  const { variant, recordEvent } = useABVariant({
    experiment: 'hero_headline',
    variants: ['A', 'B', 'C'],
  });
  const headlineVariant = (variant && baseCopy[variant]) || baseCopy.A;
  const newCount = useMemo(
    () => features.filter((f) => f.isNew).length,
    [features]
  );

  // Reset to default when prop changes (e.g. when mounted on a route that
  // wants to lock to one side).
  const initialAud = useRef(defaultAudience);
  useEffect(() => {
    if (initialAud.current !== defaultAudience) {
      setAudience(defaultAudience);
      initialAud.current = defaultAudience;
    }
  }, [defaultAudience]);

  // Record one impression per (audience × variant × bucket) per page mount
  const sentImpressionFor = useRef(null);
  useEffect(() => {
    if (!variant || !bucket) return;
    const key = `${audience}:${variant}:${bucket}`;
    if (sentImpressionFor.current === key) return;
    sentImpressionFor.current = key;
    recordEvent('impression', { audience, bucket });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, audience, bucket]);

  const onToggle = (next) => {
    if (next === audience) return;
    recordEvent('toggle', { audience: next, bucket });
    setAudience(next);
  };

  return (
    <section
      className="relative overflow-hidden rounded-3xl"
      data-testid="features-hero-card"
    >
      {/* Layered background — deliberately avoids generic purple-on-white */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a0f2e] via-[#2d1655] to-[#0f0820]" />
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 20%, rgba(167, 107, 245, 0.35) 0%, transparent 45%),' +
          'radial-gradient(circle at 85% 70%, rgba(255, 153, 102, 0.25) 0%, transparent 50%),' +
          'radial-gradient(circle at 50% 100%, rgba(112, 66, 204, 0.4) 0%, transparent 55%)',
      }} />
      {/* Subtle grain texture for depth */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative px-6 sm:px-10 lg:px-14 py-10 sm:py-14">
        {/* Top row — eyebrow + audience toggle.
            The eyebrow pill uses `max-w-full` + `flex-wrap` on mobile so the
            long copy can wrap gracefully rather than overflow the pill and
            push the internal badges into a broken vertical stack (which is
            what the "1 new · vA · UK" mobile bug looked like). */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 self-start max-w-full rounded-2xl sm:rounded-full bg-white/10 backdrop-blur px-3 py-1.5 sm:py-1 ring-1 ring-white/20">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#a76bf5] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#a76bf5]" />
            </span>
            <span className="text-[10px] sm:text-xs font-medium text-white/90 tracking-wider uppercase leading-tight">
              {baseCopy.eyebrow}
            </span>
            {newCount > 0 && (
              <Badge className="bg-white text-[#7042cc] hover:bg-white text-[10px] font-bold px-1.5 py-0 shrink-0">
                {newCount} new
              </Badge>
            )}
            {/* Internal A/B variant marker — only visible when the URL has
                `?abdebug=1`. Was leaking to real mobile visitors as a broken
                "vA · UK" stack before Feb 2026. */}
            {variant && typeof window !== 'undefined'
              && new URLSearchParams(window.location.search).get('abdebug') === '1' && (
              <span className="text-[10px] text-white/40 font-mono shrink-0" aria-hidden>
                v{variant}{bucket ? ` · ${bucket}` : ''}
              </span>
            )}
          </div>

          {allowToggle && (
            <div
              className="inline-flex items-center rounded-full bg-white/10 backdrop-blur p-1 ring-1 ring-white/20 self-start"
              role="tablist"
              aria-label="Audience toggle"
            >
              {[
                { id: 'buyer', label: 'For buyers' },
                { id: 'agent', label: 'For agents' },
              ].map((t) => {
                const active = audience === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => onToggle(t.id)}
                    className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-all duration-200 ${
                      active
                        ? 'bg-white text-[#7042cc] shadow-md'
                        : 'text-white/80 hover:text-white'
                    }`}
                    data-testid={`features-hero-tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Headline */}
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.05] tracking-tight max-w-3xl"
          style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
          data-testid="features-hero-headline"
          data-variant={variant || 'A'}
        >
          {headlineVariant.titleA}{' '}
          <span className="bg-gradient-to-r from-[#ffb37a] via-[#ff9a66] to-[#ffd29c] bg-clip-text text-transparent">
            {headlineVariant.titleB}
          </span>
        </h2>
        <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
          {baseCopy.sub}
        </p>

        {/* Stats row */}
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3" data-testid="features-hero-stats">
          {baseCopy.stats.map((s, i) => (
            <div key={i} className="flex items-baseline gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                {s.value}
              </span>
              <span className="text-xs text-white/60 uppercase tracking-wider">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Press / social proof strip — honest empty state */}
        <PressStrip tone="dark" />

        {/* CTA row — placed BEFORE the grid for above-the-fold conversion */}
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to={baseCopy.primary.to} onClick={() => recordEvent('click_primary', { audience, bucket })}>
            <Button
              className="bg-white text-[#7042cc] hover:bg-[#fff7eb] hover:text-[#5b35a8] shadow-lg shadow-purple-900/40 font-semibold px-6 py-5 text-sm sm:text-base rounded-full group"
              data-testid="features-hero-primary-cta"
            >
              {baseCopy.primary.label}
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
          <Link to={baseCopy.secondary.to} onClick={() => recordEvent('click_secondary', { audience, bucket })}>
            <Button
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/60 font-semibold px-6 py-5 text-sm sm:text-base rounded-full"
              data-testid="features-hero-secondary-cta"
            >
              {baseCopy.secondary.label}
            </Button>
          </Link>
        </div>

        {/* Feature tiles */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {features.map((f, i) => (
            <FeatureTile key={`${audience}-${i}`} feature={f} idx={i} audience={audience} />
          ))}
        </div>

        {/* Footer line — addresses revenue/trust concerns */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/60">
          <p>
            Every feature on this card is <span className="text-white font-medium">live today</span> — not a roadmap.
            {audience === 'agent' ? ' Try free for 30 days, cancel anytime.' : ' Free forever for buyers.'}
          </p>
          <p className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            GDPR-grade · {market.code === 'GB' ? 'Propertymark-aligned' : market.code === 'US' ? 'state-licensed' : 'PSRA-aligned'} · independent
          </p>
        </div>
      </div>
    </section>
  );
};

export default FeaturesHeroCard;
